/* Lucid Light — theme behaviour. Ported from design/concepts-r7/nav-c/script.js.
   Carousel, nav (morph / panel / auto-hide), scent finder and search come across
   unchanged; the concept's cart stub is replaced with a real add that goes
   through Horizon's cart events. See the "Add to cart (real)" block below.

   Loaded as a module so it can use Horizon's importmap for @shopify/events.
   That also means it is deferred by default — every DOM query in here runs
   after parse, which is why none of the concept's guards needed changing. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* ================= Horizontal swipe, shared =================
   ONE gesture implementation for every carousel in this file, defined at module
   scope so each IIFE below can reach it (a function declaration hoists, so the
   order of the IIFEs does not matter).

   It exists because the three carousels here do NOT share an implementation and
   never will: the seasonal band is a state machine over four absolutely
   positioned cards, the product gallery is a cross-fade stack, and the about
   banner redirects a horizontal drag into PAGE scroll. What they do share is the
   gesture — and a gesture written three times is a gesture debugged three times.
   So the discrete carousels take this, and the about banner keeps its own
   scroll-driving variant (which is a different behaviour, not a different
   spelling of the same one).

   What this must get right, all of it learned the hard way in this file:

   - AXIS LOCK. Decide x-or-y once, after AXIS_LOCK px of travel, and never
     re-decide mid-gesture — re-deciding makes a diagonal drag stutter between
     two behaviours. A y-lock hands the gesture straight back to the browser so
     the page scrolls exactly as it would with no handler attached.

   - touch-action: pan-y pinch-zoom, set HERE rather than in the stylesheet.
     Without it the browser can commit to a scroll on the first move and then
     deliver `cancelable: false` touchmoves, so preventDefault silently stops
     working and the swipe intermittently does nothing. It is set from script,
     not CSS, because it must apply only where a handler is actually attached —
     a one-photograph gallery has no swipe and should keep default behaviour.
     `pinch-zoom` is kept deliberately: page pinch is normal here. This is a
     different question from .pm-zoom-frame's `touch-action: none`, which exists
     to stop iOS carrying a visual-viewport scale across navigations — nothing
     here touches that.

   - THE EDGE ZONE IS LEFT ALONE. iOS reads a drag from the screen edge as
     back/forward navigation and a page cannot cancel it. Same EDGE constant as
     the about banner, same reason.

   - A CANCELLED GESTURE MUST LEAVE NOTHING BEHIND. touchcancel/pointercancel
     fire when the browser steals the gesture; without a reset the next touch
     inherits a stale origin and jumps.

   - A SWIPE IS NOT A TAP. The product slides open a zoom dialog on click, and a
     touchend after a real drag can still synthesise one. Swallowing the next
     click in the capture phase is the only reliable guard, and it has to be
     time-boxed so a genuine tap a moment later still works. */
function attachSwipe(el, onSwipe) {
  if (!el || typeof onSwipe !== "function") return;

  var EDGE = 26;        // px reserved at each side for the OS back-swipe
  var AXIS_LOCK = 8;    // px before committing to an axis
  var MIN_DIST = 34;    // px of travel that makes a drag a swipe rather than a twitch
  var FLICK_MS = 300;   // a short, fast flick counts even if it is under MIN_DIST
  var FLICK_DIST = 18;
  var CLICK_GUARD_MS = 500;

  var x0 = 0, y0 = 0, t0 = 0, axis = "", active = false, swiped = 0;

  /* Only the element that carries a handler gets its touch-action narrowed. */
  el.style.touchAction = "pan-y pinch-zoom";

  function reset() { active = false; axis = ""; }

  function onStart(e) {
    reset();
    if (e.touches.length !== 1) return;
    var t = e.touches[0];
    if (t.clientX < EDGE || t.clientX > (window.innerWidth || 0) - EDGE) return;
    x0 = t.clientX; y0 = t.clientY;
    t0 = e.timeStamp || Date.now();
    active = true;
  }

  function onMove(e) {
    if (!active || e.touches.length !== 1) return;
    var t = e.touches[0];
    var dx = t.clientX - x0, dy = t.clientY - y0;
    if (!axis) {
      if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
      axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (axis === "y") { active = false; return; }   // hand back to the browser
    }
    /* Locked to x: nothing on the page should scroll from this gesture. The
       cancelable check is not paranoia — a browser that already started a
       scroll sends uncancelable moves, and calling preventDefault on those
       logs a console violation while doing nothing. */
    if (e.cancelable) e.preventDefault();
  }

  function onEnd(e) {
    if (!active || axis !== "x") { reset(); return; }
    var t = (e.changedTouches && e.changedTouches[0]) || null;
    reset();
    if (!t) return;
    var dx = t.clientX - x0;
    var dt = (e.timeStamp || Date.now()) - t0;
    var far = Math.abs(dx) >= MIN_DIST;
    var flick = dt <= FLICK_MS && Math.abs(dx) >= FLICK_DIST;
    if (!far && !flick) return;
    swiped = Date.now();
    /* Swipe LEFT means "show me the next one", the same direction of travel a
       physical stack of cards has. Matches what the next arrow does. */
    onSwipe(dx < 0 ? 1 : -1);
  }

  /* A cancel is a stop, never a swipe: the browser took the gesture over and
     whatever the finger did after that was not aimed at this element. */
  function onCancel() { reset(); }

  el.addEventListener("touchstart", onStart, { passive: true });
  el.addEventListener("touchmove", onMove, { passive: false });
  el.addEventListener("touchend", onEnd, { passive: true });
  el.addEventListener("touchcancel", onCancel, { passive: true });
  el.addEventListener("pointercancel", onCancel, { passive: true });

  /* Capture phase, on the element, so it runs before any click handler bound to
     a descendant — which is where the zoom-opening listener lives. */
  el.addEventListener("click", function (e) {
    if (Date.now() - swiped > CLICK_GUARD_MS) return;
    swiped = 0;
    e.preventDefault();
    e.stopPropagation();
  }, true);
}

/* Lucid Light — Round 3, v3-both (quiz modal + release carousel).
   Vanilla JS, no framework, no network calls. Scent data mirrors
   design/scents.json exactly — no invented notes. */

(function () {
  "use strict";

  /* ================= The Reading (quiz modal) ================= */

  /* Scents come from the store, not from this file.

     In the concept this was a hardcoded object mirroring design/scents.json,
     and keeping those two in step is precisely what failed: when the year-round
     line changed by two candles the quiz went on scoring and recommending two
     products that no longer existed. sections/lucid-quiz.liquid renders the map
     from real products, so a discontinued candle disappears from the quiz by
     itself and a renamed handle surfaces as a missing scent rather than a wrong
     recommendation.

     Falls back to an empty map rather than throwing: no blob means this page has
     no quiz section, and the guard below then declines to start. */
  var SCENTS = (function () {
    var el = document.querySelector('[data-lucid-scents]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  })();

  /* Tie-break order — a DECISION, not an accident.

     This used to be Object.keys(SCENTS), i.e. whatever order the scents happened
     to be typed in, which is alphabetical. topScents() caps the result at two, so
     that order silently decided which two candles survive every multi-way tie —
     and multi-way ties are 22% of all answer paths. Alphabetical meant Awakened
     Rose and Clementine Bliss would have been the answer for roughly a fifth of
     shoppers, for no reason anyone chose.

     Ordered here by breadth of appeal, so a tie that means "the questions did not
     discriminate" still surfaces something broadly sellable rather than pushing
     the strangest candle at someone who gave no signal for it. Reorder freely —
     that is the point of it being written down. Must be carried into the theme
     (PORT-PLAN Phase 4); losing it there re-introduces the same invisible bias. */
  var SCENT_ORDER = [
    "vanilla-dream", "lavender-haze", "clementine-bliss", "daydream", "awakened-rose",
    "tranquil-pine", "opaline-tide", "fractal-garden", "midnight-resin", "mushroom-forest"
  ];

  /* ================= Add to cart (real) =================
     The concept carried a sessionStorage counter here purely so the conversion
     path could be judged end to end. None of it survives, as PORT-PLAN Phase 4
     said it would not. This is the genuine article.

     It deliberately goes through Horizon's CartLinesUpdateEvent rather than
     hand-rolling a fetch. That event is what the cart drawer, the cart icon and
     the cart-items component all listen to — dispatching it means the badge,
     the drawer contents and the drawer's own open/close choreography come free
     and stay correct. A bare fetch to /cart/add.js would add the line and leave
     every one of those showing stale state.

     The drawer's auto-open is deferred when the add originates inside an open
     modal <dialog> (cart-drawer.js #handleCartLinesUpdate), so it can restore
     focus before stealing it. The scent finder IS such a dialog, which is why
     this closes it on success — leave it open and the drawer never appears. */
  var ADDED_MS = 1500;

  function addLabelFor(btn) {
    return btn.getAttribute('data-added-label') || 'Added';
  }

  /* Cancel a pending "Added" restore. Called before any rewrite of the label,
     otherwise a timer armed by a previous add fires afterwards and stamps the
     old label back over the new one. */
  function resetAdded(btn) {
    if (!btn) return;
    if (btn.llAddTimer) { window.clearTimeout(btn.llAddTimer); btn.llAddTimer = null; }
    btn.classList.remove('is-added');
    btn.style.minWidth = '';
  }

  function flashAdded(btn) {
    var restore = btn.llAddTimer ? btn.llAddLabel : btn.textContent;
    /* Pin the width before shortening the label. "Added" is narrower than
       either real label, so without this the button snaps in on click and back
       out a second later — the one moment it should look most settled. */
    var w = Math.ceil(btn.getBoundingClientRect().width);
    resetAdded(btn);
    if (w) btn.style.minWidth = w + 'px';
    btn.llAddLabel = restore;
    btn.textContent = addLabelFor(btn);
    btn.classList.add('is-added');
    btn.llAddTimer = window.setTimeout(function () {
      btn.textContent = restore;
      btn.classList.remove('is-added');
      btn.llAddTimer = null;
    }, ADDED_MS);
  }

  /* items: [{id, quantity}]. Returns a promise so callers can sequence on it. */
  function addToCart(items, source) {
    items = items.filter(function (i) { return i && i.id; });
    if (!items.length) return Promise.resolve(null);

    var deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(function (i) {
        return { merchandiseId: String(i.id), quantity: i.quantity || 1 };
      }),
      promise: deferred.promise
    }));

    var url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        /* Shopify answers an add failure with 4xx AND a body carrying `status`,
           so a thrown-on-!ok check and a body check are not interchangeable —
           check the body. Out-of-stock arrives here, not in .catch. */
        if (data && data.status) {
          deferred.reject(new Error(data.description || data.message || 'Add to cart failed'));
          return null;
        }
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                items: cart.items,
                itemCount: cart.item_count,
                source: source || 'lucid',
                didError: false
              }
            });
            return cart;
          });
      })
      .catch(function (err) {
        deferred.reject(err);
        return null;
      });
  }

  document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      /* Comma-separated, because the scent finder's CTA adds BOTH candles when
         the result ties — two different variants, one each, not quantity two of
         one. A quantity attribute could not express that, and getting it wrong
         would put the wrong thing in someone's cart rather than merely looking
         odd. Single-product buttons are just the one-element case. */
      var raw = btn.getAttribute('data-variant-id');
      if (!raw) return;
      var qty = parseInt(btn.getAttribute('data-add-qty') || '1', 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      var lines = raw.split(',').map(function (s) { return s.trim(); })
        .filter(Boolean)
        .map(function (vid) { return { id: vid, quantity: qty }; });
      if (!lines.length) return;
      btn.disabled = true;
      addToCart(lines, 'lucid-button')
        .then(function (cart) {
          btn.disabled = false;
          if (!cart) return;
          flashAdded(btn);

          /* Close the host dialog on success. Horizon's drawer deliberately
             defers its auto-open while a modal <dialog> is up, so it can restore
             focus before taking it — meaning if the scent finder stays open the
             drawer never appears and the add looks like it did nothing. The
             flash is left visible for a beat first so the confirmation is seen
             before the panel goes. */
          var host = btn.closest('dialog[open]');
          if (host && typeof host.close === 'function') {
            window.setTimeout(function () { host.close(); }, 450);
          }
        });
    });
  });

  /* ================= Size picker (product page) =================
     Not in the concept — see lucid-additions.css for why it exists.

     Keeps three things in step with the chosen size: the weight line, the add
     button's variant, and its disabled state. The weight line matters more than
     it looks: it is the only place the page states what is actually in the jar,
     so leaving it describing a different variant than the button adds is how
     someone buys the wrong size and blames the shop. */
  (function () {
    var sizes = document.querySelector('[data-product-sizes]');
    if (!sizes) return;
    var weight = document.querySelector('[data-product-weight]');
    var addBtn = document.querySelector('[data-product-cta]');
    /* The weight line became a three-cell spec strip (review card
       lucid-burn-time-2026-08-23), so this element now holds ONLY the size.
       Wax is its own cell and never changes; burn time is its own cell and
       hides outright when the chosen size has no figure. */
    var burnCell = document.querySelector('[data-burn-cell]');
    var burnText = document.querySelector('[data-burn-text]');

    sizes.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio' || !input.checked) return;

      var label = input.parentElement.querySelector('.size-name');
      if (weight && label) weight.textContent = label.textContent.trim();

      /* Absent is a real state, not a zero. Empty string all the way through
         rather than a placeholder, so the cell can simply not be there. */
      var burn = (input.getAttribute('data-burn') || '').trim();
      if (burnText) burnText.textContent = burn;
      if (burnCell) burnCell.hidden = !burn;
      if (addBtn) {
        resetAdded(addBtn);
        var buyable = !input.disabled && !!input.value;
        addBtn.setAttribute('data-variant-id', buyable ? input.value : '');
        addBtn.disabled = !buyable;
        var text = addBtn.getAttribute(buyable ? 'data-add-label' : 'data-sold-out-label');
        if (text) addBtn.textContent = text;
      }
    });
  })();

  var dialog = document.getElementById("quiz-modal");
  var openBtn = document.querySelector("[data-quiz-open]");

  /* Questions are built from assets/quiz-bank.json — the same file the copy
     worksheet's builder edits and simulates every answer path against. Building
     them here rather than generating markup at push time keeps the bank the only
     place the quiz is authored; a generated intermediate is one more artifact
     that can quietly go stale, which is the failure this project keeps hitting.

     The DOM produced is byte-for-byte the shape the concept hand-wrote, so the
     quiz logic below is untouched and still reads [data-quiz-q] / data-scent. */
  function buildQuestions(bank, host, live) {
    var keys = live && live.length ? live : [];
    var chosen = keys.length
      ? keys.map(function (k) {
          return bank.questions.filter(function (q) { return q.key === k; })[0];
        }).filter(Boolean)
      : bank.questions.slice(0, 3);

    host.textContent = "";
    chosen.forEach(function (q, qi) {
      var block = document.createElement("div");
      block.className = qi === 0 ? "quiz-q" : "quiz-q hide";
      block.setAttribute("data-quiz-q", "");

      var h = document.createElement("h3");
      h.textContent = q.head;
      block.appendChild(h);

      var opts = document.createElement("div");
      opts.className = "quiz-options";
      q.answers.forEach(function (a) {
        var b = document.createElement("button");
        b.type = "button";
        b.setAttribute("data-scent", Object.keys(a.weights).map(function (id) {
          return id + ":" + a.weights[id];
        }).join(","));
        b.textContent = a.label;
        opts.appendChild(b);
      });
      block.appendChild(opts);
      host.appendChild(block);
    });
    return chosen.length;
  }

  var quizHost = dialog && dialog.querySelector("[data-quiz-questions]");

  /* The CTA is painted server-side but the quiz cannot open until the bank has
     loaded, so there is a real window — small, not zero — where clicking it does
     nothing. Rather than hide the CTA and pop it in later (which moves the hero
     under the cursor), record the click and honour it once the quiz is wired. */
  var pendingOpen = false;
  function notePendingOpen() { pendingOpen = true; }
  if (openBtn) openBtn.addEventListener("click", notePendingOpen);

  if (dialog && quizHost && openBtn) {
    fetch(quizHost.getAttribute("data-bank") || (window.LucidQuizBankUrl || ""))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (bank) {
        /* A missing or malformed bank leaves the modal with no questions, so
           don't open a door onto an empty room — hide the entry point instead.
           Failing loudly in the console and quietly in the UI is the right split
           here: the shopper loses a nice-to-have, not the page. */
        /* Also refuse to start when no scent in the bank matches a real product.
           That is the state on a store whose catalogue has not been imported yet,
           and without this check the quiz asks three questions and then throws
           building a card for a scent it knows nothing about — the worst of the
           three possible outcomes, because it fails at the end. */
        var known = SCENT_ORDER.filter(function (id) { return SCENTS[id]; }).length;
        if (!bank || !bank.questions || !known || !buildQuestions(bank, quizHost, bank.live)) {
          openBtn.hidden = true;
          return;
        }
        initQuiz();
      })
      .catch(function () { openBtn.hidden = true; });
  }

  function initQuiz() {
    if (!(dialog && openBtn && typeof dialog.showModal === "function")) return;
    var panel = dialog.querySelector(".quiz-panel");
    var closeBtn = dialog.querySelector("[data-quiz-close]");
    var qBlocks = Array.prototype.slice.call(dialog.querySelectorAll("[data-quiz-q]"));
    var progressText = dialog.querySelector("[data-quiz-progress]");
    var resultPanel = dialog.querySelector("[data-quiz-result]");
    var resultCards = dialog.querySelector("[data-result-cards]");
    var resultEyebrow = dialog.querySelector("[data-result-eyebrow]");
    var retakeBtn = dialog.querySelector("[data-quiz-retake]");
    var resultAdd = dialog.querySelector("[data-result-add]");

    var scores = {};
    var current = 0;
    var lastFocused = null;

    function resetScores() {
      scores = {};
      SCENT_ORDER.forEach(function (id) { scores[id] = 0; });
    }
    resetScores();

    function showQuestion(i) {
      qBlocks.forEach(function (b, idx) { b.classList.toggle("hide", idx !== i); });
      resultPanel.classList.remove("show");
      if (progressText) progressText.textContent = "Question " + Math.min(i + 1, qBlocks.length) + " of " + qBlocks.length;
    }

    /* Return every scent sharing the top score, capped at two.

       A tie is not a failure here — two candles scoring equally IS the honest
       answer, and showing both gives the shopper a choice instead of an
       arbitrary pick (operator call 2026-08-13). It also fixes a real defect:
       when only the single winner was shown, ties resolved by object-key order,
       which is alphabetical, and several scents could never be recommended to
       anyone. Simulating every answer path confirms all 10 are reachable.

       Capped at two deliberately: a 3+ way tie means the questions failed to
       discriminate, and listing five candles reads as a shrug rather than a
       reading. The extras are dropped rather than shown — which is exactly why
       SCENT_ORDER above has to be a chosen order and not an incidental one. */
    function topScents() {
      var bestScore = -1;
      SCENT_ORDER.forEach(function (id) {
        if (scores[id] > bestScore) bestScore = scores[id];
      });
      /* Only ever return scents that resolve to a real product. A scent in the
         bank with no matching product would otherwise win a path and then have
         no name, notes, link or variant to render. */
      return SCENT_ORDER.filter(function (id) {
        return scores[id] === bestScore && SCENTS[id];
      }).slice(0, 2);
    }

    /* Build one card per scent. Every reading renders through this — a tie is
       two cards, not a headline plus a footnote, so the second scent is never
       presented as the runner-up (operator call 2026-08-13). Structure mirrors
       the index's .ledger-card: swatch, name, notes, format.

       The heading carries the tabindex so it can take programmatic focus for the
       step change. Do NOT move that onto the anchor — tabindex="-1" there would
       pull the scent link out of the tab order. */
    function readingCard(id, first) {
      var s = SCENTS[id];
      var card = document.createElement("div");
      card.className = "reading-card";

      var head = document.createElement("h3");
      head.className = "reading-head";
      if (first) head.setAttribute("tabindex", "-1");

      var swatch = document.createElement("span");
      swatch.className = "reading-swatch";
      swatch.setAttribute("aria-hidden", "true");
      swatch.style.background = s.ink;

      var link = document.createElement("a");
      link.className = "reading-name";
      link.href = s.url || "#";     // real product URL, rendered server-side by
      link.textContent = s.name;    // sections/lucid-quiz.liquid

      head.appendChild(swatch);
      head.appendChild(link);

      var notes = document.createElement("p");
      notes.className = "reading-notes";
      notes.textContent = s.notes;

      /* Branded divider between the name and the notes — the label's own tapered
         rule, mirrored around a dot, in place of the plain hairline that used to
         sit there. Both wings are the same asset; the right one is flipped in
         CSS rather than shipped as a second file, so there is only ever one
         drawing to keep in step with the labels. */
      var rule = document.createElement("div");
      rule.className = "reading-rule";
      rule.setAttribute("aria-hidden", "true");
      ["rule-wing", "rule-dot", "rule-wing rule-wing--flip"].forEach(function (cls) {
        var part = document.createElement("span");
        part.className = cls;
        rule.appendChild(part);
      });

      /* No format line. Size is not what the reading is answering — it surfaces
         in the cart, where it is actually actionable (operator call 2026-08-13). */
      card.appendChild(head);
      card.appendChild(rule);
      card.appendChild(notes);
      return card;
    }

    function showResult() {
      var ids = topScents();
      qBlocks.forEach(function (b) { b.classList.add("hide"); });

      resultCards.textContent = "";
      ids.forEach(function (id, i) {
        resultCards.appendChild(readingCard(id, i === 0));
      });

      /* The result screen sells, it does not merely inform: the CTA adds the
         reading to the cart, and each scent name is its own link through to the
         product page (operator call 2026-08-13). "Both", not "All" — topScents()
         caps at two by construction, so "all" would never name more than two. */
      if (resultAdd) {
        resetAdded(resultAdd);
        resultAdd.textContent = ids.length > 1
          ? (resultAdd.getAttribute("data-label-plural") || "Add Both To Cart")
          : (resultAdd.getAttribute("data-label-single") || "Add To Cart");

        /* One variant per scent, quantity one each — NOT quantity two of a single
           product. Sold-out scents are dropped rather than added and rejected by
           Shopify after the click; if that empties the list the button goes away,
           because a CTA that cannot succeed is worse than no CTA. */
        var variants = ids.map(function (id) {
          var s = SCENTS[id];
          return s && s.available && s.variantId ? s.variantId : null;
        }).filter(Boolean);

        resultAdd.setAttribute("data-variant-id", variants.join(","));
        resultAdd.setAttribute("data-add-qty", "1");
        resultAdd.hidden = variants.length === 0;
      }

      /* One label, not two. The progress line and the panel eyebrow both used to
         carry this and sat stacked; once the count could vary they also
         disagreed. The eyebrow carries it; the progress line clears. */
      if (resultEyebrow) {
        resultEyebrow.textContent = ids.length > 1
          ? (resultEyebrow.getAttribute("data-plural") || "Your matches")
          : (resultEyebrow.getAttribute("data-single") || "Your match");
      }
      if (progressText) progressText.textContent = "";
      resultPanel.classList.add("show");
      // move focus to the first card's heading — a sighted+SR-friendly step change
      var head = resultCards.querySelector(".reading-head");
      if (head) head.focus();
    }

    dialog.querySelectorAll(".quiz-options button").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var pairs = btn.getAttribute("data-scent").split(",");
        pairs.forEach(function (pair) {
          var parts = pair.split(":");
          var id = parts[0];
          var weight = parseInt(parts[1] || "1", 10);
          if (scores.hasOwnProperty(id)) scores[id] += weight;
        });
        if (current < qBlocks.length - 1) {
          current += 1;
          showQuestion(current);
          qBlocks[current].querySelector("button").focus();
        } else {
          showResult();
        }
      });
    });

    if (retakeBtn) {
      retakeBtn.addEventListener("click", function () {
        resetScores();
        current = 0;
        showQuestion(0);
        qBlocks[0].querySelector("button").focus();
      });
    }

    function focusableEls() {
      return Array.prototype.slice.call(
        panel.querySelectorAll('button:not([disabled]), a[href]')
      ).filter(function (el) { return el.offsetParent !== null; });
    }

    function trapKeydown(e) {
      if (e.key === "Tab") {
        var items = focusableEls();
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      // Escape is handled natively by <dialog>, which fires 'close'.
    }

    function openQuiz() {
      lastFocused = document.activeElement;
      resetScores();
      current = 0;
      showQuestion(0);
      document.body.classList.add("quiz-scroll-lock");
      dialog.showModal();
      dialog.addEventListener("keydown", trapKeydown);
      var items = focusableEls();
      (items[0] || dialog).focus();
    }

    function closeQuiz() {
      dialog.close();
    }

    dialog.addEventListener("close", function () {
      document.body.classList.remove("quiz-scroll-lock");
      dialog.removeEventListener("keydown", trapKeydown);
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    });

    // click on the ::backdrop (the dialog element itself, outside the panel)
    dialog.addEventListener("click", function (e) {
      if (e.target === dialog) closeQuiz();
    });

    openBtn.addEventListener("click", openQuiz);
    /* Drop the placeholder listener and honour a click that landed while the
       bank was still in flight, so an eager shopper is not silently ignored. */
    openBtn.removeEventListener("click", notePendingOpen);
    if (pendingOpen) { pendingOpen = false; openQuiz(); }
    if (closeBtn) closeBtn.addEventListener("click", closeQuiz);

    // Review-only auto-open so a closed modal isn't invisible in screenshots.
    // ?quiz=1 — not part of the shipped UX, just a screenshot hook. This
    // script tag sits at the end of <body>, so the DOM is already parsed;
    // no need to wait for DOMContentLoaded.
    if (/[?&]quiz=1\b/.test(location.search)) {
      openQuiz();

      /* &reading=single|both|added — drive through to a result screen so the
         end state can be captured. Which answers produce a tie depends on the
         weights, and hardcoding a path would rot the moment they change, so we
         search the real buttons for one instead. Review hook only. */
      var want = (location.search.match(/[?&]reading=(\w+)/) || [])[1];
      var opts = qBlocks.map(function (b) {
        return Array.prototype.slice.call(b.querySelectorAll("button"));
      });
      // Guarded on 3: the lineup and the question set are both due to change
      // (PORT-PLAN 0.4), and a review hook should go quiet, not throw.
      if (want && opts.length === 3) {
        var wantCards = want === "single" ? 1 : 2;
        var found = false;
        for (var a = 0; a < opts[0].length && !found; a++) {
          for (var b2 = 0; b2 < opts[1].length && !found; b2++) {
            for (var c = 0; c < opts[2].length && !found; c++) {
              retakeBtn.click();
              opts[0][a].click(); opts[1][b2].click(); opts[2][c].click();
              found = resultCards.children.length === wantCards;
            }
          }
        }
        if (want === "added" && resultAdd) resultAdd.click();
      }
    }
  }

  /* ================= Release carousel (c1-faithful) =================
     Reference: reference/figma-seasonal-carousel.png. Same state machine
     as the base concept (current/next/prev absolute-positioned cards) —
     what's new here is dot-accent colouring (each season's brand-neon,
     replacing the reference's orange) and ArrowLeft/ArrowRight support,
     since the reference's dot row + circular arrows are keyboard-operable
     controls, not decoration. */

  /* FALLBACK ONLY — the real values are read from the section below. Keeping a
     copy here is what broke the dots: the palette rework moved the blue from
     winter to summer and gave winter an indigo, and this map went on colouring
     the active dot with the old pairing because nothing connects the two. */
  var ACCENTS = { winter: "#50C0E0", spring: "#80E080", summer: "#C06CCC", autumn: "#E8B06C" };

  // Same --season-ink values set inline on each .season-card, mirrored here
  // so the mobile prev/next icon (see SEASON_ICONS below) can be coloured
  // toward the season it's navigating to — dark tone on thin stroke
  // linework, per the same principle behind the .is-live badge fix.
  var SEASON_INK = { winter: "#1F6E80", spring: "#3D6B41", summer: "#6E2A7D", autumn: "#8A4A1E" };

  /* Overwrite both from the section's own payload, which already carries an
     accent and an ink for every season because the cards need them. One source
     of truth; a palette change in the theme editor now reaches the dots and the
     mobile arrow icons without anyone remembering this file exists. */
  (function () {
    var el = document.querySelector("[data-lucid-seasons]");
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent) || {}; } catch (e) { return; }
    Object.keys(data).forEach(function (k) {
      if (data[k] && data[k].accent) ACCENTS[k] = data[k].accent;
      if (data[k] && data[k].ink) SEASON_INK[k] = data[k].ink;
    });
  })();

  // Mobile icon slot gets the full .season-badge treatment (pale fill +
  // ink ring), not just a coloured stroke — see render() below, which sets
  // both --icon-ink and --icon-fill. The fill expression is written as a
  // literal color-mix() string (not pre-computed here) so var(--paper)
  // still resolves in CSS at render time, matching .season-badge's own
  // `color-mix(in srgb, var(--season-accent) 18%, var(--paper))` exactly
  // instead of duplicating the paper hex in JS.
  function iconFill(id) {
    var accent = ACCENTS[id];
    return accent ? "color-mix(in srgb, " + accent + " 18%, var(--paper))" : "";
  }

  // Real display names, for the arrow aria-labels (mobile swaps the glyph
  // for a season icon, so the label is the only thing left carrying the
  // meaning — it has to stay accurate as the active season changes).
  var SEASON_NAMES = { winter: "Winter", spring: "Spring", summer: "Summer", autumn: "Autumn" };

  // Same three icons as the badges on each card (stroke-width bumped to
  // match the badge fix), reused here so mobile's next/prev buttons show
  // "the icon of the one next in the list" per operator direction, instead
  // of a chevron that was colliding with the peek cards' text.
  var SEASON_ICONS = {
    winter: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M20 6 L20 34 M8 13 L32 27 M8 27 L32 13"/><path d="M20 6 L16 10 M20 6 L24 10 M20 34 L16 30 M20 34 L24 30"/><path d="M8 13 L9 18 M8 13 L13 12 M32 27 L31 22 M32 27 L27 28"/><path d="M8 27 L9 22 M8 27 L13 28 M32 13 L31 18 M32 13 L27 12"/></svg>',
    spring: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M20 32 L20 16"/><path d="M20 20 C14 20 11 15 12 9 C18 9 21 13 20 20 Z"/><path d="M20 24 C26 24 29 20 29 15 C23 15 20 18 20 24 Z"/></svg>',
    summer: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><circle cx="20" cy="20" r="7"/><path d="M20 4 L20 9 M20 31 L20 36 M4 20 L9 20 M31 20 L36 20 M9 9 L12.5 12.5 M27.5 27.5 L31 31 M9 31 L12.5 27.5 M27.5 12.5 L31 9"/></svg>',
    autumn: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M27 11 C31 19 27 29 18 31 C13 25 15 15 22 11 C24 10 26 10 27 11 Z"/><path d="M25 13 L15 30"/><path d="M22 18 L18 17 M20 23 L16 23"/><path d="M15 30 C13 32 11 33 9 33"/></svg>'
  };

  var stage = document.querySelector("[data-carousel-track]");
  if (stage) {
    var order = ["winter", "spring", "summer", "autumn"];
    var cards = {};
    order.forEach(function (id) {
      cards[id] = stage.querySelector('[data-season="' + id + '"]');
    });
    var dots = Array.prototype.slice.call(document.querySelectorAll("[data-carousel-tab]"));
    var prevBtn = document.querySelector("[data-carousel-prev]");
    var nextBtn = document.querySelector("[data-carousel-next]");
    var prevIconSlot = prevBtn && prevBtn.querySelector("[data-arrow-icon]");
    var nextIconSlot = nextBtn && nextBtn.querySelector("[data-arrow-icon]");
    var carouselSection = document.querySelector(".calendar-section");
    // Default to whichever season is LIVE, read from the markup — not a
    // hardcoded id. Only one card carries data-live="true", and when the
    // rotation moves (a different Shopify collection published) the default
    // follows automatically with no JS edit. Falls back to the first card.
    var liveCard = stage.querySelector('[data-season][data-live="true"]');
    var liveId = liveCard && liveCard.getAttribute("data-season");
    var index = Math.max(0, order.indexOf(liveId));

    function render() {
      order.forEach(function (id, i) {
        var card = cards[id];
        // With four seasons, only the focal card and its immediate neighbours
        // should be visible. Anything further round the ring gets "far" and is
        // hidden by CSS — otherwise the 4th card stacks up beside the "prev"
        // peek and you see two cards to the left of the focal one.
        var offset = (i - index + order.length) % order.length;
        var state;
        if (offset === 0) state = "current";
        else if (offset === 1) state = "next";
        else if (offset === order.length - 1) state = "prev";
        else state = "far";
        card.setAttribute("data-state", state);
        card.setAttribute("aria-hidden", i === index ? "false" : "true");
      });
      dots.forEach(function (dot, i) {
        var selected = i === index;
        dot.setAttribute("aria-selected", selected ? "true" : "false");
        dot.style.setProperty("--dot-accent", ACCENTS[order[i]] || "");
      });

      // Arrow buttons stay real <button>s with accurate labels at every
      // breakpoint; only the decorative glyph/icon face differs by CSS
      // media query (.arrow-glyph vs .arrow-icon). The icon markup and
      // label both point at whichever season that button will move to.
      var prevId = order[(index - 1 + order.length) % order.length];
      var nextId = order[(index + 1) % order.length];
      if (prevBtn) prevBtn.setAttribute("aria-label", "Previous: " + SEASON_NAMES[prevId]);
      if (prevIconSlot) {
        prevIconSlot.innerHTML = SEASON_ICONS[prevId];
        prevIconSlot.style.setProperty("--icon-ink", SEASON_INK[prevId] || "");
        prevIconSlot.style.setProperty("--icon-fill", iconFill(prevId));
      }
      if (nextBtn) nextBtn.setAttribute("aria-label", "Next: " + SEASON_NAMES[nextId]);
      if (nextIconSlot) {
        nextIconSlot.innerHTML = SEASON_ICONS[nextId];
        nextIconSlot.style.setProperty("--icon-ink", SEASON_INK[nextId] || "");
        nextIconSlot.style.setProperty("--icon-fill", iconFill(nextId));
      }
    }

    function go(delta) {
      index = (index + delta + order.length) % order.length;
      render();
    }

    if (prevBtn) prevBtn.addEventListener("click", function () { go(-1); });
    if (nextBtn) nextBtn.addEventListener("click", function () { go(1); });
    dots.forEach(function (dot, i) {
      dot.addEventListener("click", function () { index = i; render(); });
    });

    /* Swipe is the arrows, nothing more: same go(), same wrap, same dot and
       collection-band updates downstream. Attached to the stage rather than the
       whole .calendar-section so a drag over the dot row or the copy beneath it
       still scrolls the page normally.

       NOT gated on prefers-reduced-motion. The arrows are not gated either, and
       this is a discrete state change, not an animation — the stylesheet already
       drops the card transitions under reduce, so a reader who asked for no
       motion gets an instant swap rather than no carousel. */
    attachSwipe(stage, go);

    // Arrow-key navigation — scoped to the carousel section so it doesn't
    // hijack arrow keys used elsewhere on the page (e.g. inside the quiz).
    if (carouselSection) {
      carouselSection.addEventListener("keydown", function (e) {
        if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
        else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      });
    }

    render();
  }
})();

/* ---------- inline seasonal collection ----------
   The carousel is this grid's control surface: selecting a season re-renders
   the collection below it.

   Data comes from the SERVER, off the real collections — see the note on
   COLLECTIONS below. design/scents-seasonal.json is where the notes were first
   transcribed from the physical labels; it is provenance, not a runtime source,
   and it has drifted (no summer group, daydream filed under spring when the
   store has it in Eternal Series). Do not read it back. A season with nothing in
   it gets an honest empty state rather than a fabricated preview. */
(function () {
  "use strict";

  /* Season data comes from the page, not from this file.

     The concept hardcoded four seasons with every scent name, note and image
     filename typed in. That is exactly what goes stale: the first time a season
     is poured, renamed or retired, the band keeps showing last year's. Rendered
     server-side by sections/lucid-seasonal.liquid from the real collections. */
  var COLLECTIONS = (function () {
    var el = document.querySelector('[data-lucid-seasons]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();

  var section = document.getElementById("collection");
  var titleEl = document.getElementById("collection-title");
  var subEl   = document.getElementById("collection-sub");
  var grid    = document.getElementById("collection-grid");
  var noticeEl = document.getElementById("collection-notice");
  if (!section || !grid) { return; }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  function renderCollection(id) {
    var c = COLLECTIONS[id];
    if (!c) { return; }

    section.style.setProperty("--collection-accent", c.accent);
    section.style.setProperty("--collection-ink", c.ink);
    titleEl.textContent = c.title;
    subEl.textContent = c.sub;

    // One collection is live at a time; the rest are resting and say when they
    // return. A resting collection STILL shows its products — finding something
    // out of season and learning when it is back is the point of the rotation,
    // not a dead end.
    // THIS FILE NO LONGER KNOWS ANY OF THE WORDS. `c.word` is rendered by
    // snippets/lucid-season-state.liquid into the season payload, which is the
    // same snippet the collection pages and the /collections cards read -- so
    // the home page's band and /collections/spring cannot disagree, and there
    // is one place to change the vocabulary rather than four.
    //
    // Two things this line used to do wrong, both invisible:
    //   * `c.isCurrent !== undefined ? c.isCurrent : c.live` -- the section
    //     emitted a second key, `live`, from a hand-set flag that had drifted.
    //     isCurrent always exists, so the fallback was unreachable AND stale.
    //     That key is gone from the JSON.
    //   * "Resting " + "\u2014" + " " + c.back appended an EM DASH, written as
    //     an escape, so it survived the storefront-wide em-dash purge: a byte
    //     grep for the character never saw it, and it renders on the home page.
    //     The band notice is a badge, like the one on the collection page, and
    //     carries the word alone; the return line lives on the card above and
    //     in the empty state below, which is where it was already.
    if (noticeEl) {
      var live = !!c.isCurrent;
      noticeEl.className = live ? "collection-notice is-live" : "collection-notice";
      noticeEl.textContent = c.word || (live ? "Pouring now" : "Resting");
    }

    // "has not been poured yet" was wrong on the collection it fires for most:
    // Winter has been poured five times and its stock is archived, which is
    // exactly why the band is empty. One message, matching the badge above it.
    if (!c.products || !c.products.length) {
      grid.innerHTML = '<div class="collection-empty"><p>' + esc(c.title) +
        ' is resting. ' + esc(c.back) + '</p></div>';
      return;
    }

    // The out-of-stock treatment (option G). Keyed on STOCK, not on season:
    // a past season's leftovers are still sellable and must look sellable, and
    // a candle that sells out mid-season needs marking too. Season only decides
    // the WORDING -- "back soon" if it is still that scent's season, "back next
    // <season>" if the rotation has moved on.
    var SEASON_LABEL = { winter: "winter", spring: "spring", summer: "summer", autumn: "autumn" };

    grid.innerHTML = c.products.map(function (s) {
      var out = s.available === false;
      var tag = "";
      var mark = "";
      if (out) {
        tag = c.isCurrent
          ? "Back soon"
          : "Back next " + (SEASON_LABEL[c.season] || "season");
        // Clock inside the medallion. Two signals only -- this mark and the
        // corner tag. The accent bar deliberately stays SOLID, identical to an
        // in-stock card, so the card still reads as part of the range rather
        // than as an error state.
        mark = '<span class="oos-mark" aria-hidden="true">' +
                 '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
                   'stroke-width="1.6" stroke-linecap="round">' +
                   '<circle cx="12" cy="12" r="9"></circle>' +
                   '<path d="M12 7v5.2l3.4 2"></path>' +
                 '</svg>' +
               '</span>';
      }
      return '<a class="collection-card' + (out ? " is-out" : "") + '" href="' + esc(s.url) +
               '" style="--card-ink:' + esc(s.ink) + '"' +
               /* A COMMA, not an em dash. This is spoken, not seen, and the
                  dash it replaces was a \u2014 escape -- the same runtime-assembled
                  form that hid the band notice's em dash from the storefront
                  purge (MAP.md lists three encodings; escapes are a fourth). */
               (out ? ' aria-label="' + esc(s.name) + ", " + esc(tag) + '"' : "") + '>' +
               '<span class="collection-thumb"><img loading="lazy" src="' + esc(s.img) +
                 '" alt="' + esc(s.name) + ' candle jar">' + mark + '</span>' +
               '<span class="collection-body">' +
                 '<span class="collection-name">' + esc(s.name) + '</span>' +
                 '<p class="collection-notes">' + esc(s.notes) + '</p>' +
               '</span>' +
               (out ? '<span class="oos-tag">' + esc(tag) + '</span>' : "") +
             '</a>';
    }).join("");
  }

  // The carousel owns season state; mirror whichever card is current. A
  // MutationObserver keeps this decoupled from the carousel's own render(),
  // so neither controller has to know about the other.
  var stage = document.querySelector(".carousel-stage");
  function currentSeason() {
    var el = document.querySelector('.season-card[data-state="current"]');
    return el && el.getAttribute("data-season");
  }
  if (stage) {
    new MutationObserver(function () {
      var id = currentSeason();
      if (id) { renderCollection(id); }
    }).observe(stage, { subtree: true, attributes: true, attributeFilter: ["data-state"] });
  }
  renderCollection(currentSeason() || "spring");
})();

/* =========================================================================
   NAV C — auto-hiding mobile bottom bar + inline expanding panel.
   Two physically separate components exist in the markup: .site-header
   (desktop, hidden below 760px) and .mobile-nav (bottom-fixed, hidden above
   760px) — see styles.css. This controller only touches .mobile-nav.

   Behaviour:
   - Hamburger toggles an inline panel that unfolds directly above the bar
     (no scrim, no separate screen). Collapsed panel content is `inert`, so
     it never traps keyboard focus when closed.
   - The bar hides on a deliberate scroll-down, returns on ANY scroll-up,
     and is forced visible at the top of the page, the bottom of the page,
     and whenever the panel or any <dialog> is open.
   - Under prefers-reduced-motion: reduce, the scroll listener is never
     attached at all — the bar simply never hides, so there is no motion
     to gate and nothing to fight.
   ========================================================================= */
(function () {
  "use strict";

  var mobileNav = document.querySelector("[data-mobile-nav]");
  if (!mobileNav) return;

  var hamburger = document.querySelector("[data-panel-toggle]");
  var panel = document.getElementById("mobile-panel");
  var reducedMotionMQ = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* ---- product detail: the circle CLOSES instead of opening a menu ----
     A product page is a detail view, so its circle is an X. The markup is a
     real <a href="index.html">, which means with JS off — or storage blocked —
     it still lands you on the catalog. The upgrade below only swaps in
     history.back() when we know there IS somewhere in-site to go back to,
     which also restores the shopper's scroll position in the index.

     The breadcrumb is READ before it is written, so the first page of a
     session correctly reports "no history": a cold landing straight onto a
     product (an ad, a shared link — the dominant case for a storefront) falls
     through to the href instead of firing history.back() into whatever site
     the visitor came from. That failure mode is precisely why this is not an
     unconditional back button. */
  var closeBtn = mobileNav.querySelector("[data-close-product]");
  try {
    var store = window.sessionStorage;
    /* The WHOLE path, with its leading slash. This used to store only the last
       segment — a holdover from the concept, where pages were files called
       index.html and accessories.html. On Shopify a listing is
       /collections/oasis-collection, so that stored "oasis-collection", and
       setting it as an href on /products/clementine-bliss made the browser
       resolve it RELATIVE to /products/ — sending Close to
       /products/oasis-collection, a page that does not exist. Search is kept so
       a filtered or paginated listing comes back as it was left. */
    var here = window.location.pathname + window.location.search;
    var onProduct = !!closeBtn;

    /* Read BEFORE writing — both values describe the page we arrived FROM,
       not this one.
         ll-listing      the last non-product page seen (Accessories, Home…)
         ll-from-listing whether that page was the one immediately before this */
    var lastListing = store.getItem("ll-listing");
    var straightFromListing = store.getItem("ll-from-listing") === "1";

    if (onProduct) {
      store.setItem("ll-from-listing", "0");
    } else {
      store.setItem("ll-listing", here);
      store.setItem("ll-from-listing", "1");
    }

    /* Reject anything not rooted at "/". Sessions started before this fix hold
       a bare segment, and honouring one would reproduce the bug for the rest of
       that visit; the markup's own href is a correct destination meanwhile. */
    if (lastListing && lastListing.charAt(0) !== "/") lastListing = null;

    if (closeBtn && lastListing) {
      /* Retarget the anchor itself. This is what makes "close" land back on
         Accessories rather than the home page, and it holds even on the paths
         we never intercept — JS-disabled, a middle-click, a long-press
         "open in new tab" — because it is the real href, not a behaviour. */
      closeBtn.setAttribute("href", lastListing);
    }

    if (closeBtn) {
      closeBtn.addEventListener("click", function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        /* Prefer a true history.back() when the previous entry REALLY is that
           listing: it restores the shopper's scroll position, so they return to
           the row they were looking at rather than the top of the page.

           history.length > 1 guards open-in-a-new-tab: sessionStorage is COPIED
           into the new tab, so the breadcrumb reads as valid while there is
           nothing behind it — without this the X would be a dead end. Hopping
           product → product also fails this test, which is correct: going "back"
           there would surface the previous product, when Close means the listing.
           Both cases fall through to the retargeted href. */
        if (straightFromListing && window.history.length > 1) {
          e.preventDefault();
          window.history.back();
        }
      });
    }
  } catch (err) {
    /* sessionStorage unavailable (private mode, some file:// contexts) — the
       markup's own href="index.html" remains a correct, if less specific,
       destination and needs no listener. */
  }

  /* ---- shared-element morph: trigger circle <-> current page's icon ----
     The raised circle is not a "menu button" that happens to sit near the
     icons — it IS the current page's icon, resting in the bar. Opening lifts
     it into its slot; dismissing brings it back; choosing a different page
     sends THAT page's icon down to take the bar position instead.

     Only transform/opacity are animated, so nothing here can reflow the bar.
     Every entry point is gated on reduced-motion, and the CSS keyframes and
     transitions live inside no-preference blocks as well — belt and braces,
     because a half-applied morph would strand the circle mid-flight. */
  var circle = mobileNav.querySelector("[data-trigger-circle]");
  var sled = mobileNav.querySelector(".mnb-sled"); // panel+bar; hides on scroll
  var MORPH_MS = 260;
  var EXIT_MS = 240; // hard cap — the animation is a courtesy, navigation is the contract
  var circleHome = null; // the circle's UNtransformed viewport rect, captured on open

  /* WHICH ROW THE CIRCLE IS. One answer, and the order of these two queries is
     the whole guard.

     A bare [aria-current] is not enough and a bare [aria-current="page"] is not
     enough either, so it is both, in this order:

       * "page" first, because a panel can legitimately mark a row AND its
         ancestor. When a collection sub-page renders its branch row, the row IS
         the page and Collections is merely above it. querySelector takes the
         FIRST match in document order, and the ancestor is always first, so an
         unguarded query lands the circle on the parent. Measured on the mock at
         390x844: the trigger read "Summer" while the circle flew to the
         Collections row 55px higher, at an error of 0.00px in all three axes
         AGAINST THE WRONG ROW. Every geometric assertion passed. The only
         symptom was that the label and the landing named different pages, which
         is why the check is now "the row it landed on is the row the trigger
         names" rather than a distance.

       * [aria-current] as the fallback, because a page that is not literally in
         the menu still needs a slot: /collections/all marks Collections with
         aria-current="true" (ARIA's "you are within this"), and before that
         marking existed planMorph() found nothing and there was simply no morph
         on any collection page — panel opens normally, console clean.

     lucid-header.liquid ALSO downgrades an ancestor to data-section-current
     whenever its descendant is marked, so today there is only ever one match
     and this preference never fires. That is deliberate belt and braces: the
     markup rule keeps the panel honest, this keeps the controller honest for
     the next sub-nav that forgets it. */
  function landingLink() {
    if (!panel) return null;
    return panel.querySelector('a[aria-current="page"]') || panel.querySelector("a[aria-current]");
  }

  function currentIcon() {
    var link = landingLink();
    return link ? link.querySelector(".mnb-panel-icon") : null;
  }

  /* Read where the target slot WILL be once the nav has finished settling —
     which is not where it is at the moment of the tap, for two independent
     reasons:

       1. The panel opens by animating max-height, so its icons still report
          their COLLAPSED position.
       2. If the bar was hidden by scroll, the sled is still translated fully
          off-screen, so every icon inside it reads ~a bar-height too LOW.

     (2) was a real bug: opening the menu while the bar was scroll-hidden sent
     the circle DOWNWARD, because the flight was computed against the hidden
     frame, and on dismiss it rose back up from below. Reordering alone does not
     fix it — un-hiding starts a 0.24s slide, and getBoundingClientRect() during
     a transition returns the ANIMATED position, not the final one. So both
     transitions are suppressed, both end states are forced, the rect is read,
     and everything is put back exactly as it was for the real animation to
     play from. The double reflow is the price of landing where the slot will
     be rather than where it happens to be. */
  function measureOpen(el) {
    var wasOpen = panel.classList.contains("is-open");
    var wasHidden = mobileNav.classList.contains("is-hidden");
    var prevPanel = panel.style.transition;
    var prevSled = sled ? sled.style.transition : "";
    /* The panel is a real scroll container (overflow-y: auto, so a menu too long
       for the viewport degrades to a scroller rather than clipping). Forcing
       .is-open below grows its clientHeight, and a browser CLAMPS scrollTop when
       a scroller grows — so the act of measuring can scroll the panel and move
       the very slot being measured, and the FLIP would then be planned against a
       position the real open never reaches. Neutralised exactly like the two
       transitions on either side of it. */
    var prevScroll = panel.scrollTop;

    panel.style.transition = "none";
    if (sled) sled.style.transition = "none";
    if (!wasOpen) panel.classList.add("is-open");
    if (wasHidden) mobileNav.classList.remove("is-hidden");
    void panel.offsetHeight;

    var rect = el.getBoundingClientRect();

    if (!wasOpen) panel.classList.remove("is-open");
    if (wasHidden) mobileNav.classList.add("is-hidden");
    if (panel.scrollTop !== prevScroll) panel.scrollTop = prevScroll;
    void panel.offsetHeight; // settle BEFORE restoring, or the restore animates
    panel.style.transition = prevPanel;
    if (sled) sled.style.transition = prevSled;
    return rect;
  }

  /* `t` interpolates the flight (1 = landed). The fractional form exists so a
     headless screenshot can freeze a deterministic mid-flight frame instead of
     racing a 260ms animation — see the review hooks at the bottom. */
  function flipTransform(from, to, t) {
    /* A zero source width means the element was measured while it had no box --
       mid-restore, or hidden. Dividing by it below yields Infinity and the
       circle scales off the screen, so bail to no transform instead. */
    if (!from.width || !to.width) return "";
    var dx = (to.left + to.width / 2 - (from.left + from.width / 2)) * t;
    var dy = (to.top + to.height / 2 - (from.top + from.height / 2)) * t;
    var scale = 1 + (to.width / from.width - 1) * t;
    // translateX(-50%) replicates the circle's own centring rule; drop it and
    // the element snaps a half-width to the right the instant transform is set.
    return "translateX(-50%) translate(" + dx + "px," + dy + "px) scale(" + scale + ")";
  }

  /* Measured BEFORE .is-open is applied — see setPanelOpen. */
  function planMorph(t) {
    var icon = currentIcon();
    if (!circle || !icon) return null; // no slot on this page: no morph, no crash
    return {
      icon: icon,
      home: circle.getBoundingClientRect(),
      target: measureOpen(icon),
      t: t == null ? 1 : t
    };
  }

  function applyMorph(plan) {
    circleHome = plan.home;
    plan.icon.style.visibility = "hidden"; // the flown circle IS this icon now
    circle.style.transform = flipTransform(plan.home, plan.target, plan.t);
    circle.style.boxShadow = "none";
    mobileNav.classList.add("is-morphed");
  }

  function reverseMorph() {
    if (!circle) return;
    circle.style.transform = ""; // back to the stylesheet's translateX(-50%)
    circle.style.boxShadow = "";
    mobileNav.classList.remove("is-morphed");
    var icon = currentIcon();
    if (icon) {
      // Restore the real icon only once the circle is home, otherwise the two
      // briefly double-render in the same slot.
      window.setTimeout(function () { icon.style.visibility = ""; }, MORPH_MS);
    }
  }

  /* ---- a flight that never landed on this page ----------------------------

     flyToBar() appends a .mnb-flier to <body>, hides the panel icon behind it,
     and parks the real circle at opacity 0 — then relies on the NAVIGATION to
     take all three away. Nothing ever removes them, which is fine exactly once.

     Back-navigation is not that case. iOS restores from bfcache with the DOM as
     it was left, so swiping back to the home page brings with it a `position:
     fixed; z-index: 60` copy of a nav icon, frozen at the scale it was flying
     at, while the real circle sits invisible underneath. It presents as the page
     zooming in on the bottom nav button — which is literally what is on screen.

     Cleaned on pagehide so the SNAPSHOT is clean (by then the flight has had its
     260ms and the next page is painting), and again on a persisted pageshow for
     the case where the snapshot was taken mid-air anyway. */
  function resetFlight() {
    var fliers = document.querySelectorAll(".mnb-flier");
    for (var i = 0; i < fliers.length; i++) fliers[i].remove();
    if (circle) {
      circle.style.opacity = "";
      circle.style.transform = "";
      circle.style.boxShadow = "";
    }
    if (mobileNav) mobileNav.classList.remove("is-morphed");
    var icons = document.querySelectorAll(".mnb-panel-icon");
    for (var j = 0; j < icons.length; j++) icons[j].style.visibility = "";
  }

  window.addEventListener("pagehide", resetFlight);
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    resetFlight();
    /* Arriving back at a half-open menu is its own confusion. Close it with the
       morph suppressed, or the close itself flies something across the screen
       the instant the page appears. */
    if (panelOpen) setPanelOpen(false, true);
  });

  /* Select a DIFFERENT page: that icon enlarges and descends into the bar.
     It has to be a clone rather than the icon itself — the real one lives in
     .mobile-panel, which is `overflow: hidden` for its max-height collapse, so
     it would be sheared off the moment it left the panel box. */
  function flyToBar(link, t) {
    var icon = link.querySelector(".mnb-panel-icon");
    if (!icon || !circle || !circleHome) return false;
    var from = icon.getBoundingClientRect();
    var flier = document.createElement("span");
    flier.className = "mnb-flier";
    flier.innerHTML = icon.innerHTML;
    flier.style.left = from.left + "px";
    flier.style.top = from.top + "px";
    flier.style.width = from.width + "px";
    flier.style.height = from.height + "px";
    document.body.appendChild(flier);
    icon.style.visibility = "hidden";
    circle.style.opacity = "0"; // the parked circle belongs to the page we're leaving
    /* Counter-scale the glyph. The flier's box grows 30px -> 52px (×1.733), and
       a glyph that just rode that scale would land at 31px against the 25px the
       destination page paints — a visible pop at the exact moment of handoff.
       So the svg shrinks in its own coordinates by however much the box grows:
       18px at takeoff (matching the panel icon) down to 25/1.733 = 14.4px, which
       the box scale then renders back at exactly 25px. */
    var srcSvg = icon.querySelector("svg");
    var dstSvg = circle.querySelector("svg");
    var flySvg = flier.querySelector("svg");
    if (srcSvg && dstSvg && flySvg) {
      var scale = circleHome.width / from.width;
      // computed width, not getBoundingClientRect — the circle is mid-morph and
      // its rect would come back already multiplied by the parked scale.
      var startW = parseFloat(window.getComputedStyle(srcSvg).width);
      var endW = parseFloat(window.getComputedStyle(dstSvg).width) / scale;
      if (startW && endW) {
        var atT = t == null ? endW : startW + (endW - startW) * t;
        flySvg.style.width = flySvg.style.height = startW + "px";
        void flier.offsetHeight;
        if (t != null) flySvg.style.transition = "none";
        flySvg.style.width = flySvg.style.height = atT + "px";
      }
    }

    if (t != null) flier.style.transition = "none"; // frozen frame for review
    void flier.offsetHeight;
    if (t == null || t > 0) flier.classList.add("is-landing"); // shadow grows in
    // Same geometry as the circle's flight, minus the centring prefix: the
    // flier is placed by left/top, so it needs no translateX(-50%) baseline.
    flier.style.transform =
      flipTransform(from, circleHome, t == null ? 1 : t).replace("translateX(-50%) ", "");
    return true;
  }

  var isHidden = false;
  function setHidden(hide) {
    if (hide === isHidden) return;
    isHidden = hide;
    mobileNav.classList.toggle("is-hidden", hide);
  }

  /* ---- inline expanding panel ---- */
  var panelOpen = false;

  function setPanelOpen(open, skipMorph) {
    if (!hamburger || !panel) return;
    panelOpen = open;
    var animate = !reducedMotionMQ.matches;

    /* Measure BEFORE .is-open lands. measureOpen() has to suppress the panel's
       own transition to read final geometry, and doing that once the open
       animation is already in flight would snap the panel wide instead of
       letting it unfold. */
    var plan = open && animate ? planMorph() : null;

    hamburger.setAttribute("aria-expanded", open ? "true" : "false");
    panel.classList.toggle("is-open", open);
    panel.toggleAttribute("inert", !open);
    setHidden(false); // never hide the bar while the panel is open

    if (plan) applyMorph(plan);
    else if (!open && animate && !skipMorph) reverseMorph();

    if (open) {
      /* Land on the CURRENT page's row, not the first one.
         Two things were wrong with focusing panel.querySelector("a"). It always
         picked Home, and on iOS Safari a programmatic focus satisfies
         :focus-visible — so the Home row wore the lavender highlight every time
         the menu opened, on every page. Desktop Chrome does not match
         :focus-visible for a scripted focus, which is why it only showed on a
         phone.

         Focusing where you already are is also the better behaviour on its own:
         a menu should open oriented, not at the top of a list. */
      /* preventScroll, because the panel is a scroll container (overflow-y:
         auto). Focus normally scrolls its target into view, and that scroll lands
         AFTER planMorph() has already measured where the icon will be — so the
         circle flies to a slot that has since moved. Measured before this flag
         existed: 11px off at five menu items, 66px at six, 176px at eight.
         Nothing here needs the scroll; the row being focused is the one the
         derived max-height guarantees is already on screen. */
      /* Same resolution as the morph's, through the same helper, because these
         two disagreeing is a bug with no symptom: the circle would fly to one
         row while the keyboard focus sat on another. */
      var landing = landingLink() || panel.querySelector("a");
      if (landing) landing.focus({ preventScroll: true });
    }
  }

  if (hamburger && panel) {
    hamburger.addEventListener("click", function () {
      setPanelOpen(!panelOpen);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && panelOpen) {
        setPanelOpen(false);
        hamburger.focus();
      }
    });

    document.addEventListener("click", function (e) {
      if (!panelOpen) return;
      if (panel.contains(e.target) || hamburger.contains(e.target)) return;
      setPanelOpen(false);
    });

    /* Choosing a different page: its icon descends into the bar while the
       panel collapses, then we navigate. The morph is decorative — every path
       below still ends at `href`, and the timeout fires whether or not the
       transition ever reports finishing. */
    panel.addEventListener("click", function (e) {
      var link = e.target.closest ? e.target.closest("a") : null;
      if (!link || !panel.contains(link)) return;
      var href = link.getAttribute("href");
      if (!href || href.charAt(0) === "#") return;
      if (link.hasAttribute("aria-current")) return; // already here — plain navigation
      if (reducedMotionMQ.matches) return;
      // Never swallow a modified click: cmd/ctrl/shift/alt and middle-click all
      // mean "open elsewhere", and preventing them would break the browser.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      e.preventDefault();
      if (!flyToBar(link)) { window.location.href = href; return; }
      setPanelOpen(false, true); // collapse WITHOUT the reverse morph
      window.setTimeout(function () { window.location.href = href; }, EXIT_MS);
    });

    /* Review-only hooks, same convention as the quiz's ?quiz=1.
         ?panel=1              panel open, circle parked in its slot (end state)
         ?morph=0.5            panel open, circle frozen mid-flight at t
         ?exit=0.5&to=about    the chosen page's icon frozen mid-descent at t
       All freeze transitions off so a headless capture renders a deterministic
       frame instead of racing a 260ms animation. */
    /* ?frame=<ms> / ?exitframe=<ms>&to=<page> — start the REAL interaction, then
       pause every running transition and scrub it to <ms>. Because these are the
       actual CSSTransition objects, each channel keeps its own stylesheet
       duration and easing (panel 220ms, circle 260ms, label 180ms), so a stepped
       capture shows true overlap. Nothing here restates a timing value, so the
       preview cannot drift away from the CSS the way a hand-rolled one would. */
    /* Motion previews are captured by an EXTERNAL harness (review/capture.html)
       that drives a real tap and scrubs the resulting CSSTransitions from the
       parent frame. Deliberately not a hook here: an in-page version had to
       guess when the transitions existed, and racing that guess produced frames
       that silently showed the wrong instant. The harness can poll instead. */

    var morphAt = location.search.match(/[?&]morph=([0-9.]+)/);
    var exitAt = location.search.match(/[?&]exit=([0-9.]+)/);
    if (morphAt || exitAt || /[?&]panel=1\b/.test(location.search)) {
      panel.style.transition = "none";
      if (circle) circle.style.transition = "none";
      panel.classList.add("is-open");
      panel.removeAttribute("inert");
      hamburger.setAttribute("aria-expanded", "true");
      panelOpen = true;
      var frozen = planMorph(morphAt ? parseFloat(morphAt[1]) : 1);
      if (frozen) applyMorph(frozen);

      if (exitAt) {
        var toName = (location.search.match(/[?&]to=([a-z]+)/) || [])[1] || "about";
        var target = panel.querySelector('a[href="' + toName + '.html"]');
        if (target) flyToBar(target, parseFloat(exitAt[1]));
      }
    }
  }

  /* ---- reveal instantly whenever a dialog opens (quiz or search) ---- */
  function anyDialogOpen() {
    return !!document.querySelector("dialog[open]");
  }
  var dialogs = document.querySelectorAll("dialog");
  if (dialogs.length) {
    var dialogObserver = new MutationObserver(function () {
      if (anyDialogOpen()) { setHidden(false); }
    });
    Array.prototype.forEach.call(dialogs, function (d) {
      dialogObserver.observe(d, { attributes: true, attributeFilter: ["open"] });
    });
  }

  /* ---- auto-hide on scroll: passive listener, rAF-throttled ---- */
  var lastY = window.scrollY;
  var ticking = false;
  var DELTA_MIN = 6; // px of deliberate downward scroll before hiding
  var EDGE_GUARD = 4; // px tolerance for "at top" / "at bottom"

  function update() {
    ticking = false;
    var y = Math.max(0, window.scrollY);
    var doc = document.documentElement;
    var atTop = y <= EDGE_GUARD;
    var atBottom = (y + window.innerHeight) >= (doc.scrollHeight - EDGE_GUARD);
    var delta = y - lastY;

    if (panelOpen || anyDialogOpen() || atTop || atBottom) {
      setHidden(false);
    } else if (delta > DELTA_MIN) {
      setHidden(true);
    } else if (delta < 0) {
      setHidden(false); // any upward scroll: come back immediately
    }
    lastY = y;
  }

  if (!reducedMotionMQ.matches) {
    window.addEventListener("scroll", function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener("resize", function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  }
  // else: prefers-reduced-motion: reduce — no listener attached, bar never
  // hides. (".mobile-nav.is-hidden" is simply never applied.)
})();

/* =========================================================================
   Site search — Shopify's predictive search endpoint, debounced and
   abort-guarded, degrading to a real GET /search.

   This header used to describe a hardcoded 24-product index built from
   design/scents.json and design/scents-seasonal.json, which is what the CONCEPT
   shipped and what the code below explicitly stopped doing. It survived the
   rewrite and contradicted the first line of the IIFE. There is no baked-in
   product list here; the only local data is SEARCH_ACCENTS, and the reason for
   that is documented where it is defined.
   ========================================================================= */
(function () {
  "use strict";

  /* Predictive search, not a baked-in list.

     The concept shipped a hardcoded 24-product array with every name and note
     typed in. Right for a static concept, exactly wrong here: it goes stale the
     first time a candle is added, renamed or retired, and the failure is
     invisible — search simply never finds the new thing.

     SEARCH_ACCENTS, not ACCENTS: the carousel above already owns that name for
     its season colours, and shadowing it inside the same file would be a bug
     waiting for whoever edits this next. Accents live here at all because
     predictive search returns titles, URLs and images but NO metafields, while
     the design puts a coloured swatch on every row. The map is rendered once by
     sections/lucid-search.liquid. */
  var SEARCH_ACCENTS = (function () {
    var el = document.querySelector('[data-lucid-accents]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();

  /* Size chips on a suggestion row.

     The states come from the server (sections/lucid-search.liquid, generated by
     lucid-size-slots in its 'states' mode) rather than being worked out here,
     because predictive search returns `variants` as an empty array AND because
     a second copy of the matching rules is exactly how a search row ends up
     promising a size the product page says is not offered.

     Three characters, a/o/n, in slot order: 7.5 oz, 17 oz, wax melt. */
  var SIZE_STATES = (function () {
    var el = document.querySelector('[data-lucid-sizes]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  })();

  /* Cloned per row from one <template>, not inlined per product: the three
     glyphs are ~600 bytes of path data and there are thirteen products. */
  var SIZE_ICONS = (function () {
    var t = document.querySelector('[data-lucid-size-icons]');
    if (!t || !t.content) return [];
    return Array.prototype.slice.call(t.content.querySelectorAll('svg'))
      .map(function (n) { return n.outerHTML; });
  })();

  var SIZE_CLASS = { a: "avail", o: "out", n: "none" };

  function sizeChips(handle) {
    var st = SIZE_STATES[handle];
    if (!st || st.length !== SIZE_ICONS.length) return "";
    var out = "";
    for (var i = 0; i < st.length; i++) {
      out += '<span class="size-chip is-' + (SIZE_CLASS[st.charAt(i)] || "none") + '">' +
             SIZE_ICONS[i] + "</span>";
    }
    /* aria-hidden as a set: the row already announces the product name, and
       three more labels per suggestion turns a six-result list into eighteen
       extra things to hear before you can pick one. The sizes are reinforcement
       here, and the product page states them properly. */
    return '<span class="size-slots size-slots--inline" aria-hidden="true">' + out + "</span>";
  }

  var dialog = document.getElementById("search-modal");
  var openBtns = Array.prototype.slice.call(document.querySelectorAll("[data-search-open]"));
  if (!dialog || !openBtns.length || typeof dialog.showModal !== "function") return;

  var panel = dialog.querySelector(".search-panel");
  var closeBtn = dialog.querySelector("[data-search-close]");
  var input = dialog.querySelector("[data-search-input]");
  var results = dialog.querySelector("[data-search-results]");
  var form = dialog.querySelector("[data-search-form]");
  var lastFocused = null;

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  function swatchStyle(ink) {
    return (ink.indexOf("linear-gradient") === 0 || ink.indexOf("#") === 0) ? ink : ("#" + ink);
  }

  var DEBOUNCE_MS = 180;
  var searchTimer = null;
  var inflight = null;
  var suggestUrl = (results && results.getAttribute('data-suggest-url')) || '/search/suggest';
  var searchPageUrl = (form && form.getAttribute('action')) || '/search';
  var rootUrl = '/';
  var hintText = (results && results.textContent || '').trim() ||
                 'Search every candle, by name or by note.';

  function handleOf(url) {
    var m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function resultRow(p) {
    var ink = SEARCH_ACCENTS[handleOf(p.url)] || 'var(--ink-22)';
    var notes = String(p.body || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    /* --accent as well as --result-ink: .size-chip.is-avail paints from --accent,
       so without it every available chip falls back to plain ink and the row
       loses the one colour that tells the scents apart. */
    return '<a class="search-result" href="' + esc(p.url) +
        '" style="--result-ink:' + esc(ink) + ';--accent:' + esc(ink) + '">' +
      '<span class="search-result-swatch" aria-hidden="true"></span>' +
      '<span class="search-result-body">' +
        '<span class="search-result-name">' + esc(p.title) + '</span>' +
        (notes ? '<span class="search-result-notes">' + esc(notes.slice(0, 90)) + '</span>' : '') +
      '</span>' +
      sizeChips(handleOf(p.url)) +
    '</a>';
  }

  function paint(query, products, cols) {
    if (!products.length && !cols.length) {
      results.innerHTML = '<p class="search-empty">No matches for \u201c' + esc(query) +
        '\u201d. Try a scent note like \u201cvanilla\u201d or \u201ccitrus,\u201d or browse ' +
        '<a href="' + esc(rootUrl) + '">the full index</a>.</p>';
      return;
    }

    var total = products.length + cols.length;
    var html = '<p class="search-count">' + total + (total === 1 ? ' match' : ' matches') +
      ' for \u201c' + esc(query) + '\u201d</p>';

    /* Grouped by the shop's own product_type, so the panel keeps the concept's
       sectioned feel without inventing categories the store does not use.
       Untyped products fall into ONE UNLABELLED group rather than a group called
       "Other" — a label like that reads as a real category and is a claim the
       store never made. */
    var groups = {}, order = [];
    products.forEach(function (p) {
      var g = String(p.type || '').trim();
      if (!groups[g]) { groups[g] = []; order.push(g); }
      groups[g].push(p);
    });

    /* A label only earns its place when it SEPARATES something. The store used
       to run three product types; wax melts became a size on the scents that
       ship them, so live searches now come back Candle, Accessory, or both —
       and one group under a heading reading "Candle" tells a shopper who typed
       a candle name nothing they did not already know. Worse on a phone, where
       it costs a line of the six the panel has.

       Kept when there are two: "Candle" above the scents and "Accessory" above
       the care kit is a real distinction and worth the line. */
    var labelGroups = order.length > 1;
    order.forEach(function (g) {
      html += '<div class="search-group">' +
        (g && labelGroups ? '<p class="search-group-label">' + esc(g) + '</p>' : '') +
        groups[g].map(resultRow).join('') + '</div>';
    });

    if (cols.length) {
      html += '<div class="search-group"><p class="search-group-label">Collections</p>' +
        cols.map(function (c) {
          return '<a class="search-result" href="' + esc(c.url) + '">' +
            '<span class="search-result-body"><span class="search-result-name">' +
            esc(c.title) + '</span></span></a>';
        }).join('') + '</div>';
    }

    results.innerHTML = html;
  }

  function render(query) {
    var q = String(query || '').trim();

    if (searchTimer) { window.clearTimeout(searchTimer); searchTimer = null; }
    /* Abort the previous request instead of letting it land. Without this a slow
       early query can resolve AFTER a fast later one and overwrite the panel
       with answers to something the shopper already stopped typing. */
    if (inflight) { inflight.abort(); inflight = null; }

    if (!q) {
      results.innerHTML = '<p class="search-hint">' + esc(hintText) + '</p>';
      return;
    }

    searchTimer = window.setTimeout(function () {
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      inflight = ctrl;
      var url = suggestUrl + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection&resources[limit]=8' +
        '&resources[options][unavailable_products]=last';

      fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl && ctrl.signal })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          inflight = null;
          var res = (data && data.resources && data.resources.results) || {};
          paint(q, res.products || [], res.collections || []);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          inflight = null;
          /* A network failure is NOT "no results". Saying "no matches" here
             would be a lie that sends someone away believing the candle does
             not exist, so offer the full results page instead. */
          results.innerHTML = '<p class="search-empty">Search is not responding. ' +
            '<a href="' + esc(searchPageUrl) + '?q=' + encodeURIComponent(q) +
            '">Open the full results page</a>.</p>';
        });
    }, DEBOUNCE_MS);
  }

  function focusableEls() {
    return Array.prototype.slice.call(
      panel.querySelectorAll("input, button:not([disabled]), a[href]")
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  function trapKeydown(e) {
    if (e.key !== "Tab") return;
    var items = focusableEls();
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openSearch() {
    lastFocused = document.activeElement;
    if (input) input.value = "";
    render("");
    document.body.classList.add("search-scroll-lock");
    dialog.showModal();
    dialog.addEventListener("keydown", trapKeydown);
    if (input) input.focus();
  }

  function closeSearch() { dialog.close(); }

  dialog.addEventListener("close", function () {
    document.body.classList.remove("search-scroll-lock");
    dialog.removeEventListener("keydown", trapKeydown);
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  });

  dialog.addEventListener("click", function (e) {
    if (e.target === dialog) closeSearch();
  });

  openBtns.forEach(function (btn) { btn.addEventListener("click", openSearch); });
  if (closeBtn) closeBtn.addEventListener("click", closeSearch);
  if (form) form.addEventListener("submit", function (e) { e.preventDefault(); });
  if (input) input.addEventListener("input", function () { render(input.value); });

  // Review-only hooks, same convention as the quiz's ?quiz=1 — ?search=1
  // opens the modal for a screenshot; &q=... pre-fills and runs a query.
  if (/[?&]search=1\b/.test(location.search)) {
    openSearch();
    var qm = location.search.match(/[?&]q=([^&]*)/);
    if (qm) {
      var qVal = decodeURIComponent(qm[1].replace(/\+/g, " "));
      if (input) input.value = qVal;
      render(qVal);
    }
  }
})();

/* ================= About banner — where the random draw went =================
   The draw is no longer here. It runs from a parser-blocking inline <script>
   beside the markup in sections/lucid-about-banner.liquid, and it has to.

   This file is loaded as type="module", so it is deferred, so it executes after
   the page has already painted. Measured on /pages/about over 30 cold loads at
   1280px: first-contentful-paint at 204-568 ms, the draw committing at
   242-610 ms, paint landing FIRST on all 30 by 9 to 116 ms. A visitor watched
   the banner show three photographs and then swap them for three others. An
   inline classic script has no yield point between the track's markup and its
   own execution, so the first frame already carries the drawn set. Same fix and
   same reasoning as the Eternal Series card's `data-ci-pool` script; the probe
   is design/concepts-r8/about-banner/tools/about-slices.mjs.

   Anything the banner needs AFTER paint — the sideways pan fallback and the
   swipe handler — still belongs here, and is below. Only the draw moved.

   The pan controller no longer depends on this file having removed the losing
   slices first: it counts .ab-slice:not(.ab-extra), which is right whether the
   inline script pruned them or the CSS is still hiding them. */

/* ================= About banner — sticky sideways pan =================
   FALLBACK ONLY. Where CSS scroll-driven animations exist (Chromium since 2023,
   Safari 26 — roughly 85%) the pan is pure CSS, runs off the main thread, and
   this never attaches. This covers the rest, chiefly Firefox, which still had
   scroll-driven animations behind a flag through 2026.

   One behaviour, two engines — not two behaviours. The geometry lives in CSS
   for both: this only computes progress and writes a transform, so the two
   paths cannot drift on layout.

   Bails on reduced motion. The stylesheet already unwinds the pin there, so
   attaching would fight it — and a reader who asked for no motion should not
   have a scroll listener running on their behalf either. */
(function () {
  "use strict";

  var banner = document.querySelector("[data-about-banner]");
  if (!banner) return;

  var track = banner.querySelector("[data-ab-track]");
  if (!track) return;
  var pill = banner.querySelector("[data-ab-pill]");
  /* :not(.ab-extra) rather than a bare .ab-slice count. The draw normally
     removes the losers outright, so the two agree — but if the inline script
     did not run (a theme-editor section re-render never executes injected
     script tags) the extras are still in the DOM, display:none via
     .about-banner:not([data-ab-ready]) .ab-extra. Counting those would size
     the track and the rail for a pool that is not on screen. */
  var slices = banner.querySelectorAll(".ab-slice:not(.ab-extra)").length;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  var supportsSDA = window.CSS && CSS.supports &&
                    (CSS.supports("animation-timeline: view()") ||
                     CSS.supports("animation-timeline", "view()"));
  /* NOTE: no early return here. Only the PAN fallback is conditional — the
     swipe handler below must attach in every browser, because it redirects the
     gesture into page scroll and page scroll is what drives the CSS engine too.
     Returning early here (as this did at first) silently disabled swipe on
     Chrome and Safari, i.e. on almost every phone that would use it. */

  var ticking = false;
  var travel = 0;

  function readTravel() {
    /* Read the distance from the same custom property the CSS keyframe uses, so
       adding a fourth slice changes one number in Liquid and both engines follow. */
    var raw = getComputedStyle(banner).getPropertyValue("--ab-travel").trim();
    travel = parseFloat(raw) || 0;
  }

  function panning() {
    /* Only pan in the layout that actually pins. Matching the stylesheet's own
       breakpoint here is duplication, but the alternative — inferring it from
       computed position:sticky — reads as "sticky" on desktop too, where the
       element simply never has anywhere to stick. */
    return window.matchMedia("(max-width: 700px)").matches && !reduce.matches;
  }

  function clear() {
    track.style.transform = "";
    if (pill) pill.style.transform = "";
  }

  function update() {
    ticking = false;
    if (!panning()) { clear(); return; }

    var rect = banner.getBoundingClientRect();
    var scrollable = rect.height - window.innerHeight;
    if (scrollable <= 0) { clear(); return; }

    /* 0 when the banner's top hits the viewport top, 1 when its bottom hits the
       viewport bottom — the exact interval the sticky child is pinned for. */
    var progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
    track.style.transform = "translate3d(" + (-progress * travel) + "%,0,0)";

    /* The rail rides the SAME progress value, so the pill cannot drift out of
       step with the slice behind it — which is the whole reason the indicator
       is worth having. Travels (n-1) slots, each slot being its own width. */
    if (pill && slices > 1) {
      pill.style.transform = "translate3d(" + (progress * (slices - 1) * 100) + "%,0,0)";
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  /* ---- horizontal swipe drives the page's VERTICAL scroll ----------------
     Operator call: a horizontal strip invites a horizontal swipe, so swiping
     should advance it — and once the images run out, keep going into the text.
     One continuous forward motion whichever way the finger moves.

     The trick that makes this safe is that the swipe does NOT move the track.
     It scrolls the PAGE, and both pan engines already follow page scroll. So
     there is still exactly one source of truth, nothing can desync, and this
     works identically whether the CSS or the JS engine is driving — including
     on browsers where this file otherwise does nothing.

     It also answers "what happens past the last image" for free: there is no
     special case, because scrolling past the pin is just scrolling.

     Ratio: over the whole pin the track travels one viewport WIDTH per viewport
     HEIGHT of scroll, so vh/vw converts a finger's horizontal distance into the
     scroll that moves the image by that same distance. Drag an image's width,
     advance one image.

     EDGE ZONE IS LEFT ALONE. iOS treats a drag from the screen edge as
     back/forward navigation, and a web page cannot cancel it. Rather than fight
     a gesture we would lose to, ignore touches that start there. */
  var EDGE = 26;        // px reserved at each side for the OS back-swipe
  var AXIS_LOCK = 8;    // px before committing to an axis
  var FLING_MIN = 0.08; // px/ms of finger speed below which a release is a stop
  var FLING_DECAY = 0.94;
  var gx = 0, gy = 0, gLast = 0, gActive = false, gAxis = "";
  var gVel = 0, gTime = 0, flingId = 0;

  function stopFling() {
    if (flingId) { cancelAnimationFrame(flingId); flingId = 0; }
  }

  /* Inertia. Without it the gesture stops dead on release, which is what makes
     a horizontal swipe feel sluggish next to a vertical one even though the
     drag itself tracks the image exactly 1:1 — every native scroller coasts,
     and a flick that would carry a whole screen vertically was moving only the
     literal drag distance here. Advancing one image needs a full screen-width
     drag at 1:1, so without a fling it took three or four swipes to cross three
     images.

     Direct tracking while the finger is down, momentum after it lifts — the
     same split every native carousel uses. */
  function fling() {
    flingId = 0;
    if (Math.abs(gVel) < FLING_MIN) return;
    window.scrollBy({
      top: -gVel * 16 * (window.innerHeight / (window.innerWidth || 1)),
      left: 0, behavior: "instant"          // same reason as the drag above
    });
    gVel *= FLING_DECAY;
    flingId = requestAnimationFrame(fling);
  }

  function onTouchStart(e) {
    gActive = false; gAxis = "";
    stopFling();                       // a new touch always wins over a coast
    if (!panning() || e.touches.length !== 1) return;
    var t0 = e.touches[0];
    if (t0.clientX < EDGE || t0.clientX > window.innerWidth - EDGE) return;
    gx = gLast = t0.clientX;
    gy = t0.clientY;
    gVel = 0;
    gTime = e.timeStamp || Date.now();
    gActive = true;
  }

  function onTouchMove(e) {
    if (!gActive || e.touches.length !== 1) return;
    var t0 = e.touches[0];
    var dx = t0.clientX - gx;
    var dy = t0.clientY - gy;

    if (!gAxis) {
      if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
      /* Lock once, on the first decisive movement. Re-deciding mid-gesture
         makes a diagonal drag stutter between the two behaviours. */
      gAxis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (gAxis === "y") { gActive = false; return; }   // hand back to the browser
    }

    var vw = window.innerWidth || 1;
    var moved = t0.clientX - gLast;
    var now = e.timeStamp || Date.now();
    var dt = now - gTime;
    if (dt > 0) {
      /* Smooth the velocity estimate. A single frame's delta is noisy enough
         that a steady drag can end on an outlier and fling when it should stop. */
      gVel = (gVel * 0.7) + ((moved / dt) * 0.3);
      gTime = now;
    }
    gLast = t0.clientX;
    /* Swipe LEFT (dx negative) moves the images left — the same direction
       scrolling down moves them — so it scrolls down. */
    /* behavior:'instant', NOT a bare scrollBy. Horizon's base.css sets
       `scroll-behavior: smooth` on the document, which turns every scroll call
       into an ANIMATION — and this fires ~60x a second during a drag, so each
       call cancels the previous one before it can finish and the page creeps
       instead of tracking the finger. It presents as "a full-screen swipe moves
       the image about a tenth of its width", which looks like a broken ratio
       rather than a scroll-behaviour problem. */
    window.scrollBy({ top: -moved * (window.innerHeight / vw), left: 0, behavior: "instant" });
    if (e.cancelable) e.preventDefault();
  }

  function onTouchEnd() {
    if (gActive && gAxis === "x") { stopFling(); flingId = requestAnimationFrame(fling); }
    gActive = false; gAxis = "";
  }

  /* A cancel is NOT an end. touchcancel means the browser took the gesture away
     — an incoming call, a system sheet, an edge swipe it decided to own — and
     coasting on the last velocity it happened to see sends the page flying on
     its own, seconds after the finger left. Stop dead and clear the state
     instead; this used to share onTouchEnd and therefore used to fling. */
  function onTouchCancel() {
    stopFling();
    gActive = false; gAxis = ""; gVel = 0;
  }

  if (!reduce.matches) {
    /* passive:false on move only — it is the one that calls preventDefault, and
       a non-passive start/end would cost scroll performance for nothing. */
    banner.addEventListener("touchstart", onTouchStart, { passive: true });
    banner.addEventListener("touchmove", onTouchMove, { passive: false });
    banner.addEventListener("touchend", onTouchEnd, { passive: true });
    banner.addEventListener("touchcancel", onTouchCancel, { passive: true });
    banner.addEventListener("pointercancel", onTouchCancel, { passive: true });
  }

  if (!supportsSDA) {
    readTravel();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", function () { readTravel(); onScroll(); }, { passive: true });
    if (reduce.addEventListener) reduce.addEventListener("change", update);
    update();
  }
})();

/* ================= Contact form (placeholder, not wired) ================= */
(function () {
  "use strict";
  var form = document.querySelector("[data-contact-form]");
  if (!form) return;
  var status = form.querySelector("[data-contact-status]");
  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (status) {
      // Two sentences, not a dash clause: this string renders on /pages/contact
      // and its em dash survived the storefront purge by being a source literal
      // inside JS rather than in Liquid or a template JSON.
      status.textContent = "This form isn’t wired up yet. No message was sent.";
      status.hidden = false;
    }
  });
})();

/* ================= Product gallery + zoom =================
   NOT IN THE CONCEPT — it was drawn around a single photograph, and every real
   product has five to seven.

   FILTERING IS THE INTERESTING PART. Each variant owns exactly one media (its
   own labelled jar); the stock and glow shots belong to no variant. So a slide
   is shown when it is unowned OR owned by the size currently selected, which
   means choosing 7.5oz silently drops the 17oz jar out of the gallery rather
   than letting someone page onto a photograph of a thing they are not buying.

   Reads the size picker rather than owning variant state: [data-product-sizes]
   already exists and already drives the weight line and the add button. A
   second source of truth for "which size" is how those three end up
   disagreeing. */
(function () {
  "use strict";

  var pm = document.querySelector("[data-pm]");
  if (!pm) return;

  var stage  = pm.querySelector("[data-pm-stage]");
  var slides = Array.prototype.slice.call(pm.querySelectorAll("[data-pm-slide]"));
  var thumbs = Array.prototype.slice.call(pm.querySelectorAll("[data-pm-thumb]"));
  if (!stage || !slides.length) return;

  var sizes = document.querySelector("[data-product-sizes]");
  var index = 0;

  function selectedVariant() {
    if (!sizes) return "";
    var r = sizes.querySelector('input[type="radio"]:checked');
    return r ? r.value : "";
  }

  function visible() { return slides.filter(function (s) { return !s.hidden; }); }

  function show(i) {
    var vis = visible();
    if (!vis.length) return;
    /* Wrap rather than clamp. The arrows are the only way through on a phone
       where the thumb rail is small, and a dead button at each end reads as
       broken rather than as a boundary. */
    index = (i + vis.length) % vis.length;
    var target = vis[index];
    slides.forEach(function (s) { s.classList.toggle("is-current", s === target); });
    var pos = slides.indexOf(target);
    thumbs.forEach(function (t, n) { t.classList.toggle("is-current", n === pos); });
    /* Drives the phone strip's centring. `index` counts VISIBLE items, and
       hidden thumbs are display:none so they occupy no space — which is why the
       same number works for both and the strip cannot drift out of step with
       the photograph it is pointing at. Set unconditionally: on a desktop no
       rule reads it. */
    pm.style.setProperty("--pm-i", index);
  }

  function refilter() {
    var v = selectedVariant();
    /* Hiding other sizes' photographs is a merchant CHOICE, not a rule — the
       argument cuts both ways (nobody pages onto a jar they are not buying, but
       they also see fewer photographs), and Shopify's own themes expose it as a
       setting rather than deciding. Off, the gallery still jumps to the chosen
       size's jar; it just keeps the rest in view. */
    var filtering = pm.hasAttribute("data-pm-filter");
    slides.forEach(function (s, n) {
      var owner = s.getAttribute("data-owner") || "";
      var ok = (!filtering || owner === "" || owner === v) && !s.hasAttribute("data-pm-off");
      s.hidden = !ok;
      if (thumbs[n]) thumbs[n].hidden = !ok;
    });
    var vis = visible();
    /* Controls appear only when there is genuinely more than one photograph
       AFTER filtering — a product whose extra media all belong to other sizes
       can legitimately come down to one. */
    pm.setAttribute("data-pm-count", vis.length > 1 ? "many" : "one");
    pm.setAttribute("data-pm-ready", "");

    /* Land on the chosen size's own photograph, not wherever we happened to be.
       That jar IS the answer to "what does this size look like".

       Gated on `v` being non-empty. A product with a single Default Title
       variant renders no size picker, so selectedVariant() returns "" -- and
       "" is exactly what an UNOWNED slide carries in data-owner. The match then
       found slide 2, the first generic shot, and the summer scents opened on a
       lidless jar instead of their own labelled one. */
    var own = v ? vis.filter(function (s) { return s.getAttribute("data-owner") === v; })[0] : null;
    show(own ? vis.indexOf(own) : 0);
  }

  pm.querySelector("[data-pm-prev]").addEventListener("click", function () { show(index - 1); });
  pm.querySelector("[data-pm-next]").addEventListener("click", function () { show(index + 1); });

  /* Swipe the stage, exactly as the arrows do — including the wrap, so the
     gesture never dead-ends where the buttons do not.

     Attached to the stage and not to .pm, because .pm also contains the thumb
     rail; on a phone that rail is its own horizontally-centring strip and a drag
     across it should not double as a page turn.

     The stage's slides carry a click listener that opens the zoom dialog, which
     is precisely why attachSwipe swallows the click that a browser may
     synthesise after a drag. Without that, every swipe would end in a modal.

     Attached unconditionally rather than behind a "more than one photograph"
     check. refilter() runs LAST in this IIFE and can change the visible count on
     every size change, so any count read here would be both premature and
     stale; show() already wraps a single visible slide onto itself, so the
     callback is a no-op in that case without needing to know. */
  attachSwipe(stage, function (dir) { show(index + dir); });
  thumbs.forEach(function (t, n) {
    t.addEventListener("click", function () {
      var vis = visible(); var i = vis.indexOf(slides[n]);
      if (i > -1) show(i);
    });
  });
  if (sizes) sizes.addEventListener("change", refilter);
  pm.addEventListener("pm:refilter", refilter);

  /* ---- zoom ---------------------------------------------------------------
     Native <dialog> + showModal(), same as the scent finder: top layer, focus
     trapped by the platform, Escape handled without a keydown listener.

     tx/ty are OFFSETS FROM CENTRE, and transform-origin is the default 50% 50%.
     The first version treated them as absolute top-left coordinates while the
     frame's own grid was ALSO centring the photograph, so opening the dialog
     added half the leftover space on top of the centring. On a desktop that was
     a lean to the right; on a phone, where the photograph is width-limited and
     the slack is vertical, it dropped the jar ~125px below the frame and read
     as a broken dialog.

     Pinch is handled here rather than left to the browser, and that is not
     polish. A pinch the PAGE handles scales the visual viewport, and iOS keeps
     that scale across navigations — so pinching a candle here and then going
     back left the home page magnified with no obvious way to undo it. */
  var dlg   = document.querySelector("[data-pm-zoom]");
  var frame = dlg && dlg.querySelector("[data-pm-zoom-frame]");
  var ZTAP = 2.5, ZMAX = 5, scale = 1, tx = 0, ty = 0, img = null;

  function place() {
    if (img) img.style.transform = "translate(" + tx + "px," + ty + "px) scale(" + scale + ")";
  }
  function clamp() {
    if (!img) return;
    var f = frame.getBoundingClientRect();
    /* Half the overhang on each axis, nothing when the photograph is smaller
       than the frame — panning must never drag background into view. */
    var mx = Math.max(0, (img.clientWidth  * scale - f.width)  / 2);
    var my = Math.max(0, (img.clientHeight * scale - f.height) / 2);
    tx = Math.min(mx, Math.max(-mx, tx));
    ty = Math.min(my, Math.max(-my, ty));
  }
  function setZoomed() { frame.classList.toggle("is-zoomed", scale > 1.01); }
  function reset() { scale = 1; tx = ty = 0; setZoomed(); place(); }

  /* Re-anchor so whatever sits under (px,py) stays under it: solve
     p = c + t' + (s'/s)(p - c - t) for t'. This is what makes both pinch and
     tap-to-zoom feel like the photograph is moving rather than the window. */
  function zoomAt(next, px, py) {
    var f = frame.getBoundingClientRect();
    var cx = f.left + f.width / 2, cy = f.top + f.height / 2;
    next = Math.min(ZMAX, Math.max(1, next));
    var k = next / scale;
    tx = (px - cx) - k * ((px - cx) - tx);
    ty = (py - cy) - k * ((py - cy) - ty);
    scale = next;
    setZoomed(); clamp(); place();
  }

  function open(slide) {
    if (!dlg || !frame) return;
    var src = slide.querySelector("img");
    if (!src) return;
    frame.textContent = "";
    img = new Image();
    img.alt = src.alt || "";
    /* currentSrc is what the browser actually chose off the srcset, so the
       dialog opens the resolution already in cache rather than starting a
       second download of a different one. */
    img.src = src.currentSrc || src.src;
    img.addEventListener("load", function () { reset(); });
    frame.appendChild(img);
    dlg.showModal();
  }

  slides.forEach(function (s) {
    s.addEventListener("click", function () { open(s); });
  });

  if (dlg && frame) {
    dlg.querySelector("[data-pm-zoom-close]").addEventListener("click", function () { dlg.close(); });
    /* Clicking the backdrop closes. The dialog's own box is the click target for
       anywhere inside it, so compare against its rect rather than event.target,
       which would treat the padding as outside. */
    dlg.addEventListener("click", function (e) {
      var r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });
    dlg.addEventListener("close", function () { frame.textContent = ""; img = null; });

    /* One pointer pans, two pinch, and a pointer that never really moved is a
       tap. Tracked in a map rather than as a single `down` flag because a
       second finger arriving has to be noticed, not ignored. */
    var pts = {}, live = 0, pinch = null, moved = 0;
    function list() { var a = []; for (var k in pts) a.push(pts[k]); return a; }
    function gap(a, b) { return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y)); }

    function onDown(e) {
      frame.setPointerCapture(e.pointerId);
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      live++;
      if (live === 1) moved = 0;
      if (live === 2) { var l = list(); pinch = { d: gap(l[0], l[1]), s: scale }; }
    }
    function onMove(e) {
      var p = pts[e.pointerId];
      if (!p) return;
      var dx = e.clientX - p.x, dy = e.clientY - p.y;
      p.x = e.clientX; p.y = e.clientY;
      moved += Math.abs(dx) + Math.abs(dy);
      var l = list();
      if (l.length >= 2 && pinch && pinch.d > 0) {
        zoomAt(pinch.s * (gap(l[0], l[1]) / pinch.d),
               (l[0].x + l[1].x) / 2, (l[0].y + l[1].y) / 2);
      } else if (scale > 1.01) {
        tx += dx; ty += dy; clamp(); place();
      }
    }
    function onUp(e) {
      if (!pts[e.pointerId]) return;
      delete pts[e.pointerId]; live--;
      if (frame.hasPointerCapture(e.pointerId)) frame.releasePointerCapture(e.pointerId);
      if (live < 2) pinch = null;
      if (live > 0) return;
      /* A pan or a pinch that ends is not a tap. Without the threshold every
         drag finished by toggling the zoom back off under your finger, and a
         thumb is never as still as a mouse. */
      if (moved > 10) return;
      if (scale > 1.01) reset(); else zoomAt(ZTAP, e.clientX, e.clientY);
    }

    frame.addEventListener("pointerdown", onDown);
    frame.addEventListener("pointermove", onMove);
    frame.addEventListener("pointerup", onUp);
    /* pointercancel is not optional on touch. Without it a gesture the browser
       takes over leaves that pointer in the map forever, and the next drag
       jumps by however far the finger travelled while nobody was listening.
       Poisoning `moved` first stops the cancel being read as a tap. */
    frame.addEventListener("pointercancel", function (e) { moved = 1e9; onUp(e); });

    /* Rotating the phone while zoomed changes the frame under a transform that
       was clamped against the old one, stranding the photograph off-screen. */
    window.addEventListener("resize", function () {
      if (dlg.open && img) { clamp(); place(); }
    }, { passive: true });
  }

  refilter();
})();

/* ================= Cart badge =================
   THE BUG THIS FIXES: the badge was server-rendered and nothing else.
   sections/lucid-header.liquid paints `<span class="cart-count">{{ cart.item_count }}</span>`
   twice — once in the desktop header, once in the mobile bottom bar — and no
   line of script in this theme ever wrote to either of them again. Every add
   was therefore correct in Shopify, correct in the drawer, and wrong in the
   badge until the next full page load. Measured on the live store before the
   fix: server item_count=1, badges ["0","0"], aria-labels "Cart, 0 items".

   WHY IT WASN'T INHERITED. Horizon does have a live badge — snippets/cart-bubble.liquid
   plus the <cart-icon> component in assets/cart-icon.js, which listens for
   cartLinesUpdate and reconciles on pageshow. But cart-bubble is rendered by
   snippets/header-actions.liquid, and this theme replaces Horizon's header
   section outright (sections/header-group.json contains only lucid-header and
   lucid-search). So the component that keeps the number honest is simply not on
   the page, and its absence is invisible: the first paint is always right.

   Three separate ways the number goes stale, and all three need answering:

     1. An async add. Everything in this theme mutates the cart through
        CartLinesUpdateEvent, whose `promise` resolves with the settled cart —
        which is exactly what the drawer and cart-items already wait on. One
        listener covers the product CTA, the scent finder's two-candle add, the
        drawer's quantity steppers and the /cart page alike.

     2. A bfcache restore. Back-navigation hands the reader a DOM snapshot taken
        before the add, so the badge shows a number that was true minutes ago.
        Confirmed on the live store: pageshow.persisted=true, badges ["2","2"],
        real item_count 3. This is the "sometimes requires refresh" half of the
        report — refreshing works precisely because it bypasses the snapshot.
        A restore can be arbitrarily old, so it re-reads /cart.js rather than
        trusting anything cached in the page.

     3. A page served from Shopify's CDN with a stale item_count baked in.
        Horizon guards this with a short-lived sessionStorage handshake rather
        than a request on every page view; the same key and the same shape are
        used here, so if Horizon's cart-icon is ever reinstated the two agree
        instead of fighting. The 10s window is Horizon's, kept deliberately:
        beyond that the server value is the more trustworthy of the two.

   Only the badges this theme owns are touched. Horizon's own cart-bubble refs
   are left alone so that a page which does render one keeps its animation and
   its hidden-at-zero behaviour instead of getting two controllers. */
(function () {
  "use strict";

  var COUNTS = ".cart-count";
  var BTNS = ".cart-btn";
  var KEY = "cart-count";          // same key cart-icon.js uses
  var FRESH_MS = 10000;            // same window cart-icon.js uses

  function nodes(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }

  function paint(n) {
    if (typeof n !== "number" || !isFinite(n) || n < 0) return;
    nodes(COUNTS).forEach(function (el) { el.textContent = String(n); });
    /* The badge itself is aria-hidden, so the count only reaches a screen
       reader through the link's label. Leaving that behind is not a cosmetic
       miss — it is the whole announcement being wrong. Rebuilt rather than
       regex-patched so the plural stays right at 1. */
    nodes(BTNS).forEach(function (el) {
      el.setAttribute("aria-label", "Cart, " + n + " item" + (n === 1 ? "" : "s"));
    });
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ value: String(n), timestamp: Date.now() }));
    } catch (e) { /* private mode — the badge still works, it just cannot hand off */ }
  }

  function fromServer() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (c) { paint(c.item_count); return c.item_count; })
      .catch(function () { /* offline: keep whatever is on screen */ });
  }

  /* Cheap reconcile, no request. Only acts when the stored value is both recent
     and different from what is painted. */
  function reconcileFromSession() {
    var first = document.querySelector(COUNTS);
    if (!first) return;
    var raw;
    try { raw = sessionStorage.getItem(KEY); } catch (e) { return; }
    if (!raw) return;
    try {
      var s = JSON.parse(raw);
      if (s.value === first.textContent.trim()) return;
      if (Date.now() - s.timestamp >= FRESH_MS) return;
      var n = parseInt(s.value, 10);
      if (n >= 0) paint(n);
    } catch (e) { /* malformed — ignore rather than clear someone else's key */ }
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    if (!event || !event.promise) return;
    event.promise.then(function (res) {
      var cart = res && res.cart, detail = res && res.detail;
      /* totalQuantity is the Storefront-shaped field the event carries; itemCount
         is the AJAX-API shaped one. Both appear in practice depending on who
         dispatched, which is why cart-icon.js reads both — and `?? 0` rather
         than `|| 0` so an emptied cart is not mistaken for a missing field. */
      var n = (cart && cart.totalQuantity != null) ? cart.totalQuantity
            : (detail && detail.itemCount != null) ? detail.itemCount
            : null;
      if (n == null) return fromServer();
      paint(n);
    }).catch(function (err) {
      /* A rejected add (out of stock) leaves the cart as it was, so there is
         nothing to repaint. An AbortError is a superseded request, not a
         failure. Anything else: ask the server rather than guess. */
      if (err && err.name === "AbortError") return;
      fromServer();
    });
  });

  window.addEventListener("pageshow", function (e) {
    if (e.persisted) fromServer();   // a restored snapshot can be any age
    else reconcileFromSession();
  });

  reconcileFromSession();
})();
